// Header component

import React, { useState } from 'react';
import { Menu, MapPin, Settings, HelpCircle, Bell, X } from 'lucide-react';
import Button from '../ui/Button';

const Header = ({ onMenuToggle, searchQuery, onSearchChange, className = '' }) => {
  const [localSearch, setLocalSearch] = useState(searchQuery || '');

  const handleSearch = (e) => {
    const value = e.target.value;
    setLocalSearch(value);
    onSearchChange?.(value);
  };

  const clearSearch = () => {
    setLocalSearch('');
    onSearchChange?.('');
  };

  return (
    <header
      className={`
        bg-white border-b border-dark-200
        px-4 py-3
        flex items-center justify-between
        sticky top-0 z-40
        ${className}
      `}
    >
      {/* Left section */}
      <div className="flex items-center gap-4">
        <button
          onClick={onMenuToggle}
          className="p-2 rounded-lg text-dark-600 hover:bg-dark-100 lg:hidden"
          aria-label="Toggle menu"
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Logo */}
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
            <MapPin className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-primary-500">FRONTSEAT</h1>
            <p className="text-xs text-dark-500 -mt-0.5">Outdoor Advertising</p>
          </div>
        </div>
      </div>

      {/* Center section - Search */}
      <div className="hidden md:flex items-center flex-1 max-w-xl mx-8">
        <div className="relative w-full">
          <input
            type="text"
            placeholder="Search by brand (e.g., Burger King) or area..."
            value={localSearch}
            onChange={handleSearch}
            className="w-full pl-10 pr-10 py-2 bg-dark-50 border border-dark-200 rounded-lg
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
              transition-all duration-200"
          />
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          {localSearch && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400 hover:text-dark-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Right section */}
      <div className="flex items-center gap-2">
        <button
          className="p-2 rounded-lg text-dark-600 hover:bg-dark-100 relative"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5" />
          <span className="absolute top-1 right-1 w-2 h-2 bg-secondary-500 rounded-full" />
        </button>

        <button
          className="p-2 rounded-lg text-dark-600 hover:bg-dark-100 hidden sm:block"
          aria-label="Help"
        >
          <HelpCircle className="w-5 h-5" />
        </button>

        <button
          className="p-2 rounded-lg text-dark-600 hover:bg-dark-100 hidden sm:block"
          aria-label="Settings"
        >
          <Settings className="w-5 h-5" />
        </button>

        {/* Generate Report Button */}
        <Button
          variant="primary"
          size="small"
          className="hidden sm:flex"
        >
          Generate Report
        </Button>
      </div>
    </header>
  );
};

export default Header;
